import { Card } from "@/components/Card";
import { Outlet, useLocation } from "react-router-dom";
import { getAppConfig } from "@/utils/appManager";
import { Button } from "@/components/Button";
import { useTranslation } from "react-i18next";
import { faExternalLink } from "@fortawesome/pro-duotone-svg-icons";
import { H5 } from "@/components/Typography";
import { checkIsChristmas } from "@/utils/helpers";
import Snowfall from "react-snowfall";
import datashipsLogo from "@/assets/svgs/dataships-logo.svg";
import datashipsXmasLogo from "@/assets/svgs/dataships-logo-xmas-light.svg";

const AuthLayout = () => {
  const { t } = useTranslation();
  const { website, shopifyAppInstallUrl } = getAppConfig();
  const { pathname } = useLocation();
  const isChristmas = checkIsChristmas();

  const logo = isChristmas ? datashipsXmasLogo : datashipsLogo;

  const renderLogo = () => (
    <a href={website} className="hover:opacity-75">
      <img
        src={logo}
        alt="Dataships Logo"
        width={170}
        height={80}
        className="mb-40"
      />
    </a>
  );

  const renderTextAndDivider = () => (
    <>
      <H5 className="mb-15 font-medium text-white">
        {t("auth.control_panel")}
      </H5>
      <hr className="mb-25 w-50 border-t-2 border-white" />
    </>
  );

  const renderAppInstallLink = () =>
    pathname.includes("/auth/login") && (
      <div className="mt-20 flex justify-center">
        <Button
          as="a"
          target="_blank"
          href={shopifyAppInstallUrl}
          variant="link-light"
          width="full"
          icon={faExternalLink}
          iconPosition="end"
          iconClassName="h-15">
          {t("auth.install_from_app_store")}
        </Button>
      </div>
    );

  const renderCardWithOutlet = () => (
    <div className="mb-25 w-full md:w-[450px]">
      <Card rounded="md">
        <div className="p-20">
          <Outlet />
          {renderAppInstallLink()}
        </div>
      </Card>
    </div>
  );

  const renderBasicLink = ({ url, text }: { url: string; text: string }) => (
    <a className="hover:underline" target="_blank" href={url}>
      {text}
    </a>
  );

  const renderLinks = () => (
    <p className="mb-40 text-white">
      {renderBasicLink({ url: `${website}/privacy-centre`, text: "Privacy" })} &{" "}
      {renderBasicLink({ url: `${website}/terms-of-business`, text: "Terms" })}
    </p>
  );

  const renderCopyright = () => {
    const year = new Date().getFullYear();
    return <p className="text-white">&copy; {year} Copyright Dataships</p>;
  };

  const renderSnowfall = () =>
    isChristmas ? (
      <Snowfall color="white" style={{ background: "transparent" }} />
    ) : null;

  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center bg-primary bg-gradient-to-br from-primary to-green p-20">
      {renderLogo()}
      {renderTextAndDivider()}
      {renderCardWithOutlet()}
      {renderLinks()}
      {renderCopyright()}
      {renderSnowfall()}
    </div>
  );
};

export default AuthLayout;
